import { ALT_TEXT_GENERATION_WIDGETS_MAP } from 'constants/altTextTagWidgets';
import {
  digestMessage,
  getDataUnit,
  getFormatValue,
  mapSeriesDataToTabularData,
  seriesDataToTabularData,
  singleSeriesDataToTabularData,
  widgetTypeToKeyMap,
} from 'hooks/useAltTextGeneration/useAltTextGeneration.helpers';
import { Client } from 'libs/e2api/Client';
import { useE2Client } from 'modules/E2ClientProvider';
import { useAppSelector } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { useCallback, useState } from 'react';
import { WidgetType } from 'types/widget.types';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { HistogramChartWidgetData } from 'widgets/ChartWidgets/HistogramWidget/histogramChartWidget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { MapWidgetData } from 'widgets/MapWidget/MapWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

const cache = new Map<string, string>();

const handleImageAltTextGeneration = async (client: Client, imageWidgetData: ImageWidgetData) => {
  if (!imageWidgetData.url) {
    throw new Error('Image widget does not have a url');
  }

  // Check if we have cached the caption for this image
  if (cache.has(imageWidgetData.url)) {
    return {
      altText: cache.get(imageWidgetData.url),
      url: imageWidgetData.url,
      type: widgetTypeToKeyMap[WidgetType.Image],
    };
  }

  // TODO: improve crop image caption generation
  const { altText } = await client.generateImageCaption(imageWidgetData.url);

  if (altText) {
    cache.set(imageWidgetData.url, altText);
  }

  return {
    altText,
    url: imageWidgetData.url,
    type: widgetTypeToKeyMap[WidgetType.Image],
  };
};

const handleChartAltTextGeneration = async (
  client: Client,
  widgetType: WidgetType,
  chartWidgetData: ChartWidgetData,
) => {
  const { seriesData, numberFormat, dataLabels } = chartWidgetData;
  const data = seriesDataToTabularData(seriesData);

  if (!data) throw new Error('Invalid chart data');

  const type = widgetTypeToKeyMap[widgetType];
  const params = {
    type,
    /**
     * TODO: use xAxis/yAxis title or find near relevant title text widget for better alt text generation
     */
    title: '',
    unit: getDataUnit(numberFormat, dataLabels),
    data,
  };

  const cacheKey = await digestMessage(JSON.stringify(params));

  // Check if we have cached the alt text for this chart
  if (cacheKey && cache.has(cacheKey)) {
    return {
      altText: cache.get(cacheKey),
      type,
    };
  }

  const { altText } = await client.generateChartAltText(params);

  if (cacheKey && altText) {
    cache.set(cacheKey, altText);
  }

  return {
    altText,
    type,
  };
};

const handleMapAltTextGeneration = async (client: Client, mapWidgetData: MapWidgetData) => {
  const { seriesData, mapType, region, legend, series } = mapWidgetData;
  const data = mapSeriesDataToTabularData(seriesData);

  if (!data) throw new Error('Invalid map data');

  const params = {
    region: region || '',
    legend: (legend?.enabled && legend?.title?.text) || '',
    mapType,
    formatValue: getFormatValue(series.dataLabels),
    data,
  };

  const cacheKey = await digestMessage(JSON.stringify(params));

  // Check if we have cached the alt text for this chart
  if (cacheKey && cache.has(cacheKey)) {
    return {
      altText: cache.get(cacheKey),
      type: mapType,
    };
  }

  const { altText } = await client.generateMapAltText(params);

  if (cacheKey && altText) {
    cache.set(cacheKey, altText);
  }

  return {
    altText,
    type: mapType,
  };
};

const handleHistogramAltTextGeneration = async (client: Client, histogramWidgetData: HistogramChartWidgetData) => {
  const { seriesData, dataLabels } = histogramWidgetData;

  const data = singleSeriesDataToTabularData(seriesData);

  if (!data) throw new Error('Invalid histogram data');

  const type = widgetTypeToKeyMap[WidgetType.HistogramChart];
  const params = {
    title: '',
    type,
    formatValue: getFormatValue(dataLabels),
    data,
  };

  const cacheKey = await digestMessage(JSON.stringify(params));

  // Check if we have cached the alt text for this chart
  if (cacheKey && cache.has(cacheKey)) {
    return {
      altText: cache.get(cacheKey),
      type,
    };
  }

  const { altText } = await client.generateChartAltText(params);

  if (cacheKey && altText) {
    cache.set(cacheKey, altText);
  }

  return {
    altText,
    type,
  };
};

export const useAltTextGeneration = (widgetId: string) => {
  const { client } = useE2Client();
  const [isGenerating, setIsGenerating] = useState(false);

  const widgetType = getWidgetTypeFromId(widgetId);
  const widgetData = useAppSelector(selectWidget(widgetId));

  const generateAltText = useCallback(async (): Promise<
    { altText?: string; type: string; url?: string } | undefined
  > => {
    try {
      if (!client) return;

      setIsGenerating(true);

      switch (true) {
        case widgetType === WidgetType.Image:
          return await handleImageAltTextGeneration(client, widgetData as ImageWidgetData);
        case widgetType === WidgetType.MapChart:
          return await handleMapAltTextGeneration(client, widgetData as MapWidgetData);
        case widgetType === WidgetType.HistogramChart:
          return await handleHistogramAltTextGeneration(client, widgetData as HistogramChartWidgetData);
        case ALT_TEXT_GENERATION_WIDGETS_MAP.has(widgetType):
          return await handleChartAltTextGeneration(client, widgetType, widgetData as ChartWidgetData);
        default:
          break;
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsGenerating(false);
    }
  }, [client, widgetType, widgetData]);

  return {
    isGenerating,
    generateAltText,
    enabledGeneration: ALT_TEXT_GENERATION_WIDGETS_MAP.has(widgetType),
  };
};
